<script setup>
import { onBeforeMount, reactive } from 'vue'
import { useI18n } from 'vue-i18n'
import { useHttp } from '../composables/http'

const { t } = useI18n()
const $http = useHttp()

const state = reactive({
    params: {},
    loading: false,
    verified: false,
    brokenLink: false,
    alreadyVerified: false,
    error: false,
    resent: false,
})

const verifyAccount = async () => {
    state.loading = true
    try {
        await $http.get('/csrf-cookie')
        await $http.get('users/verification', { params: state.params })
        state.verified = true
    } catch (err) {
        if (err.response?.status === 409) {
            state.alreadyVerified = true
        } else {
            state.error = true
        }
    } finally {
        state.loading = false
    }
}

const resendLink = async () => {
    try {
        await $http.get('/csrf-cookie')
        await $http.post(`users/${state.params.id}/verification/code`)
        state.resent = true
    } catch (err) {
        console.error('Failed to resend', err)
    }
}

onBeforeMount(() => {
    const url = new URL(window.location.href.replace('#/', ''))
    url.searchParams.forEach((value, key) => {
        state.params[key] = value
    })
    if (!state.params.id) {
        state.brokenLink = true
    } else {
        verifyAccount()
    }
})
</script>

<template>
    <div class="mp-form text-center">
        <div v-if="state.loading" class="py-4">
            <div class="spinner-border text-primary" role="status"></div>
            <p class="text-muted mt-3">{{ t('verification.verifying') }}</p>
        </div>

        <div v-else-if="state.verified" class="py-4">
            <h5 class="mt-3 mb-2">{{ t('verification.successTitle') }}</h5>
            <p class="text-muted mb-4">{{ t('verification.successDesc') }}</p>
            <router-link :to="{ name: 'login' }" class="btn mp-btn-primary w-100">
                {{ t('verification.goToLogin') }}
            </router-link>
        </div>

        <div v-else-if="state.alreadyVerified" class="py-4">
            <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"
                style="color: #0d6efd">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
            <h5 class="mt-3 mb-2">{{ t('verification.alreadyVerifiedTitle') }}</h5>
            <p class="text-muted mb-4">{{ t('verification.alreadyVerifiedDesc') }}</p>
            <router-link :to="{ name: 'login' }" class="btn mp-btn-primary w-100">
                {{ t('verification.goToLogin') }}
            </router-link>
        </div>

        <div v-else-if="state.brokenLink || state.error" class="py-4">
            <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"
                style="color: #dc3545">
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
            <h5 class="mt-3 mb-2">{{ t('verification.errorTitle') }}</h5>
            <p class="text-muted mb-4">
                {{ state.brokenLink ? t('verification.brokenLink') : t('verification.errorDesc') }}
            </p>
            <button v-if="!state.brokenLink && !state.resent" class="btn mp-btn-primary w-100 mb-3"
                @click="resendLink">
                {{ t('verification.resendLink') }}
            </button>
            <p v-if="state.resent" class="text-success mb-3">{{ t('verification.linkResent') }}</p>
            <router-link :to="{ name: 'login' }" class="btn btn-outline-secondary w-100">
                {{ t('verification.goToLogin') }}
            </router-link>
        </div>
    </div>
</template>
